/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import Form from "../../../components/forms/Form";
import { productFields } from "../../../constants/forms/almoxarifado/productFields";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import SectionHeader from "../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useSelectOptions from "../../../hooks/useSelectOptions";
import { generateProductName } from "../../../utils/generateProductName";
import useUrlFilters from "../../../hooks/useUrlFilters";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";

const CreateProductPage = () => {
  const navigate = useNavigate();
  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      "items.condition.name": [],
      "items.category.name": [],
      "items.address.street": [],
      "items.address.alias": [],
      "items.address.number": [],
      "items.address.city": [],
      "items.address.state": [],
      deleted_at: false,
    },
    "almox_products",
  );

  // 1. Inicializa useForm passando as definições dos campos (productFields)
  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues(productFields),
    productFields,
  );

  const typeSelect = useRemoteSelectOptions({
    queryKey: ["typeOptions"],
    entityPath: entities.types.get,
    baseServiceGet,
    fieldId: "product.type_id",
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (type) => ({ value: type.id, label: type.name }),
  });

  const categorySelect = useRemoteSelectOptions({
    queryKey: ["categoryOptions"],
    entityPath: entities.categories.get,
    baseServiceGet,
    fieldId: "product.category_id",
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (category) => ({ value: category.id, label: category.name }),
  });

  const groupsSelect = useRemoteSelectOptions({
    queryKey: ["groupsOptions", typeSelect.selectedValue?.value],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      });

      const params = {
        filters,
        page,
        perPage,
        sort: { column: "id", order: "asc" },
      };

      const response = await baseServiceGet(
        entities.types.typeGroups(typeSelect.selectedValue?.value),
        params,
      );
      return response.result;
    },
    fieldId: "product.group_id",
    enabled: !!typeSelect.selectedValue?.value,
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (group) => ({ value: group.id, label: group.name }),
  });

  // Crie um array de rawOptions para o useSelectOptions
  const isPatrimonyOptionsRaw = useMemo(
    () => [
      { id: true, name: "Sim" },
      { id: false, name: "Não" },
    ],
    [],
  );

  const assetSelect = useSelectOptions({
    fieldId: "product.is_asset",
    formData,
    rawOptions: isPatrimonyOptionsRaw,
    isMulti: false,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  });

  const brandSelect = useRemoteSelectOptions({
    queryKey: ["brandOptions"],
    entityPath: entities.brands.get,
    baseServiceGet,
    fieldId: "product.brand_id",
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (brand) => ({ value: brand.id, label: brand.name }),
  });

  const selects = {
    "product.type_id": typeSelect,
    "product.category_id": categorySelect,
    "product.group_id": groupsSelect,
    "product.brand_id": brandSelect,
    "product.is_asset": assetSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  // Gera o nome do produto e atualiza o formData automaticamente
  useEffect(() => {
    const type = typeSelect.selectedValue?.label;
    const group = groupsSelect.selectedValue?.label;
    const brand = brandSelect.selectedValue?.label;

    // Basta ter o TIPO para começar a gerar o nome
    if (type) {
      const newName = generateProductName({
        type,
        brand: brand || "",
        group: group || "",
      });

      // Só dispara o handleChange se o nome realmente mudou
      if (newName !== formData.product?.name) {
        handleChange("product.name", newName);
      }
    } else {
      // Se o tipo for removido, limpa o nome
      if (formData.product?.name) {
        handleChange("product.name", "");
      }
    }
  }, [
    typeSelect.selectedValue?.label,
    groupsSelect.selectedValue?.label,
    brandSelect.selectedValue?.label,
    handleChange,
  ]);

  const createProduct = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePost(entities.products.create, data);
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        hideLoader();
        resetForm();
        navigate("/almoxarifado/produtos");
        queryClient.invalidateQueries([
          "almox_products",
          transformFiltersForUrl(filters),
          pagination,
        ]);
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = async () => {
    try {
      const cleanedProduct = cleanPayload(formData);
      const payload = {
        product: {
          ...cleanedProduct.product,
        },
      };
      return await createProduct.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar produto:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = () => {
    navigate(`/almoxarifado/produtos/`);
  };

  return (
    <MainLayout>
      <PageHeader
        title="Cadastro de Produto"
        showBackButton={true}
        backUrl="/almoxarifado/produtos/"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Cadastrar Produto"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createProduct.isPending}>
          {() => {
            const renderedSections = new Set();

            return productFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateProductPage;
